import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/portfolio/Navbar";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Clock,
  Share2,
  Check,
  Sparkles,
  ExternalLink,
  ChevronRight,
  CheckSquare,
  Square,
  Zap,
  Bot,
  Search,
  Layers,
  ArrowUpRight,
  ShieldCheck,
  AlertCircle,
  Table,
  BarChart3,
  FileSpreadsheet,
  Activity,
  Compass,
  FileText,
  Lock,
  Globe,
  Code2,
  Database,
  Award,
  Users,
  Target,
  Copy,
  CheckCircle2,
  HelpCircle,
  PhoneCall,
  Flame,
  LineChart
} from "lucide-react";

export default function AiVisibilityAuditPostPage() {
  const [copied, setCopied] = useState(false);
  const [copiedPromptIndex, setCopiedPromptIndex] = useState(null);
  const [checkedChecks, setCheckedChecks] = useState({});
  const [openFaq, setOpenFaq] = useState(null);

  const toggleCheck = (id) => {
    setCheckedChecks((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleFaq = (index) => {
    setOpenFaq((prev) => (prev === index ? null : index));
  };

  const handleCopyPrompt = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopiedPromptIndex(idx);
    setTimeout(() => setCopiedPromptIndex(null), 2000);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const pageTitle = "How Do I Audit My Website for Better AI Visibility Across ChatGPT, Google AI Overviews and Claude? | Kanna Narendra Kumar";
  const pageDesc = "Audit your website for AI search visibility across ChatGPT, Google AI Overviews, Claude, and Perplexity with this 10-point technical checklist and fan-out query framework by Kanna Narendra Kumar.";
  const pageUrl = "https://narendradigital.in/how-to-audit-a-website-for-ai-visibility/";

  useEffect(() => {
    const originalTitle = document.title;
    document.title = pageTitle;

    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (el) {
        el.setAttribute(attr, value);
      } else {
        const meta = document.createElement("meta");
        const isProp = selector.includes("property=");
        if (isProp) {
          const match = selector.match(/property="([^"]+)"/);
          if (match) meta.setAttribute("property", match[1]);
        } else {
          const match = selector.match(/name="([^"]+)"/);
          if (match) meta.setAttribute("name", match[1]);
        }
        meta.setAttribute(attr, value);
        document.head.appendChild(meta);
      }
    };

    setMeta('meta[name="description"]', "content", pageDesc);
    setMeta('meta[property="og:title"]', "content", pageTitle);
    setMeta('meta[property="og:description"]', "content", pageDesc);
    setMeta('meta[property="og:url"]', "content", pageUrl);
    setMeta('meta[property="og:type"]', "content", "article");
    setMeta('meta[property="og:image"]', "content", "https://narendradigital.in/ai-visibility-audit-og.jpg");
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", pageTitle);
    setMeta('meta[name="twitter:description"]', "content", pageDesc);
    setMeta('meta[name="twitter:image"]', "content", "https://narendradigital.in/ai-visibility-audit-og.jpg");

    // Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    let createdCanonical = false;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
      createdCanonical = true;
    }
    const originalCanonical = canonical ? canonical.getAttribute("href") : null;
    canonical.setAttribute("href", pageUrl);

    // Inject JSON-LD Schema: TechArticle + HowTo + BreadcrumbList + FAQPage
    const scriptId = "ai-visibility-audit-schema";
    let scriptTag = document.getElementById(scriptId);
    if (!scriptTag) {
      scriptTag = document.createElement("script");
      scriptTag.id = scriptId;
      scriptTag.type = "application/ld+json";
      scriptTag.text = JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "TechArticle",
            "@id": `${pageUrl}#article`,
            "isPartOf": {
              "@type": "WebSite",
              "@id": "https://narendradigital.in/#website",
              "name": "Kanna Narendra Kumar — Senior SEO & AI Search Strategist",
              "url": "https://narendradigital.in/"
            },
            "headline": "How Do I Audit My Website for Better AI Visibility Across ChatGPT, Google AI Overviews and Claude?",
            "alternativeHeadline": "Is Your Website AI-Ready? Audit It Yourself With These 10 Checks",
            "description": pageDesc,
            "image": "https://narendradigital.in/ai-visibility-audit-og.jpg",
            "datePublished": "2026-10-22T10:00:00+05:30",
            "dateModified": "2026-10-22T22:30:00+05:30",
            "mainEntityOfPage": pageUrl,
            "author": {
              "@type": "Person",
              "@id": "https://narendradigital.in/#author",
              "name": "Kanna Narendra Kumar",
              "jobTitle": "Senior SEO Specialist & AI Search Strategist",
              "url": "https://narendradigital.in/",
              "sameAs": [
                "https://www.linkedin.com/in/narendra-kumar-kanna/"
              ]
            },
            "publisher": {
              "@type": "Person",
              "name": "Kanna Narendra Kumar"
            },
            "keywords": [
              "How to audit website for AI visibility",
              "Audit website for ChatGPT Google AI Overviews Claude",
              "AI visibility audit checklist",
              "OAI-SearchBot robots.txt",
              "Claude-SearchBot Anthropic",
              "Google AI Overviews SEO",
              "Perplexity citations",
              "Generative Engine Optimization GEO",
              "Fan-out query optimization",
              "AEO Technical SEO audit"
            ],
            "articleSection": "AEO/GEO , AI Visibility",
            "inLanguage": "en-US"
          },
          {
            "@type": "HowTo",
            "@id": `${pageUrl}#howto`,
            "name": "How to Audit Your Website for Better AI Visibility Across ChatGPT, Google AI Overviews and Claude",
            "description": "A comprehensive 10-step DIY technical and content audit framework to make your website crawlable, indexable, understandable, and cited by AI answer engines.",
            "totalTime": "PT45M",
            "step": [
              {
                "@type": "HowToStep",
                "position": 1,
                "name": "Check Whether AI Search Bots Can Access Your Website",
                "text": "Inspect robots.txt, CDN/WAF firewall rules, and server logs for OAI-SearchBot, Googlebot, Claude-SearchBot, and PerplexityBot."
              },
              {
                "@type": "HowToStep",
                "position": 2,
                "name": "Check Whether Your Important Pages Are Indexed",
                "text": "Audit Google Search Console for index status, noindex tags, and canonical errors on high-value product, pricing, and comparison pages."
              },
              {
                "@type": "HowToStep",
                "position": 3,
                "name": "Check Whether Content Is Accessible Without JavaScript Problems",
                "text": "Compare original HTML source with rendered DOM to ensure pricing, features, and core answers do not depend solely on client-side JS."
              },
              {
                "@type": "HowToStep",
                "position": 4,
                "name": "Check Whether Your Content Answers Real Customer Questions",
                "text": "Map buying journey questions (problem, solution, comparison, pricing, limitations) into a 3-tier coverage matrix to satisfy AI fan-out queries."
              },
              {
                "@type": "HowToStep",
                "position": 5,
                "name": "Check Whether Your Content Is Easy to Understand and Cite",
                "text": "Structure pages with atomic direct answers, clear descriptive headings, structured data tables, and evidence-backed claims."
              },
              {
                "@type": "HowToStep",
                "position": 6,
                "name": "Check Your Canonical Tags, Redirects and Internal Links",
                "text": "Eliminate redirect hops, correct self-referencing canonicals, and strengthen internal linking between informational blogs and commercial pages."
              },
              {
                "@type": "HowToStep",
                "position": 7,
                "name": "Check Whether Search Engines Can Understand Your Business",
                "text": "Disambiguate your brand identity with clear About pages, consistent NAP/entity data, and valid Organization, Product, and Article schema."
              },
              {
                "@type": "HowToStep",
                "position": 8,
                "name": "Check Whether Your Claims Are Supported by Evidence",
                "text": "Substantiate commercial claims with measurable case studies, original research statistics, author credentials, and transparent limitations."
              },
              {
                "@type": "HowToStep",
                "position": 9,
                "name": "Check Which Competitors AI Platforms Mention Instead of You",
                "text": "Run prompt testing across ChatGPT, Claude, Google AI Overviews, and Perplexity to analyze competitor citations and content gaps."
              },
              {
                "@type": "HowToStep",
                "position": 10,
                "name": "Measure AI Visibility Before and After Making Changes",
                "text": "Track 5 separate indicators over time: AI bot requests, Google AI impressions, Bing citations, GA4 AI referral traffic, and leads."
              }
            ]
          },
          {
            "@type": "BreadcrumbList",
            "@id": `${pageUrl}#breadcrumb`,
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://narendradigital.in/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Blog",
                "item": "https://narendradigital.in/blog/"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "How to Audit Your Website for AI Visibility",
                "item": pageUrl
              }
            ]
          },
          {
            "@type": "FAQPage",
            "@id": `${pageUrl}#faq`,
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is an AI visibility audit?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "An AI visibility audit is a systematic evaluation of your website to determine whether artificial intelligence platforms (like ChatGPT Search, Google AI Overviews, Claude, and Perplexity) can crawl your pages, render your content, understand your business entity, and cite your answers when users ask relevant fan-out questions."
                }
              },
              {
                "@type": "Question",
                "name": "Which AI search crawlers should I check in my robots.txt?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You should check search-specific crawlers including OAI-SearchBot (OpenAI ChatGPT Search), Googlebot (Google AI Overviews & Search), Claude-SearchBot (Anthropic), and PerplexityBot (Perplexity Search). Be sure to distinguish search crawlers from model training crawlers like GPTBot."
                }
              },
              {
                "@type": "Question",
                "name": "Why is JavaScript rendering a critical failure point in AI visibility?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "While Googlebot can render JavaScript, not all AI crawlers or RAG pipelines execute client-side JavaScript when gathering real-time web context. If your pricing, product specifications, or direct answers only appear after client-side hydration, AI retrieval bots may only see an empty shell."
                }
              },
              {
                "@type": "Question",
                "name": "What are fan-out queries and why do they matter for AI search?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Fan-out queries are the multi-layered follow-up questions that branch out from a user's core intent (such as pricing, integrations, suitability, comparisons, and migration risks). AI systems synthesize multi-faceted answers by pulling information from sources that address this entire journey rather than ranking a single keyword page."
                }
              },
              {
                "@type": "Question",
                "name": "How much does a professional AI visibility audit cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Kanna Narendra Kumar offers a complete done-for-you Website AI Visibility Audit for a flat ₹5,000 (~$60 USD) with a 72-hour turnaround, including full bot diagnostics, JavaScript rendering parity checks, fan-out query maps, and two 1-on-1 strategy calls."
                }
              }
            ]
          }
        ]
      });
      document.head.appendChild(scriptTag);
    }

    window.scrollTo(0, 0);

    return () => {
      document.title = originalTitle;
      if (canonical) {
        if (createdCanonical) {
          canonical.remove();
        } else if (originalCanonical) {
          canonical.setAttribute("href", originalCanonical);
        }
      }
      if (scriptTag) scriptTag.remove();
    };
  }, []);

  const auditChecklistItems = [
    {
      id: "c1",
      num: "01",
      title: "AI Crawler Access",
      desc: "Verify robots.txt, WAF firewalls, and server logs allow OAI-SearchBot, Claude-SearchBot, PerplexityBot, and Googlebot."
    },
    {
      id: "c2",
      num: "02",
      title: "Google Indexability",
      desc: "Confirm core commercial, pricing, and comparison pages are indexed without accidental noindex or canonical blocks."
    },
    {
      id: "c3",
      num: "03",
      title: "JavaScript & DOM Parity",
      desc: "Check that critical answers, pricing, and specifications exist in raw HTML and do not require client-side execution."
    },
    {
      id: "c4",
      num: "04",
      title: "Customer Question Coverage",
      desc: "Map buying journey questions (problem, solution, comparison, pricing, limitations) into a 3-tier gap matrix."
    },
    {
      id: "c5",
      num: "05",
      title: "Content Citability (AEO)",
      desc: "Format key answers with atomic clarity, descriptive H2/H3s, tables, and concise summaries suitable for direct AI quote."
    },
    {
      id: "c6",
      num: "06",
      title: "Canonicals & Internal Links",
      desc: "Ensure self-referencing canonicals, eliminate redirect chains, and link high-authority blogs to commercial pages."
    },
    {
      id: "c7",
      num: "07",
      title: "Business Entity Clarity",
      desc: "Disambiguate brand identity with an About page, author credentials, social entity anchors, and valid Schema markup."
    },
    {
      id: "c8",
      num: "08",
      title: "Evidence & E-E-A-T Proof",
      desc: "Support claims with verifiable case studies, metrics, methodology transparency, and clear product limitation disclosures."
    },
    {
      id: "c9",
      num: "09",
      title: "Competitor AI SERP Audit",
      desc: "Run repeatable prompt testing across ChatGPT, Claude, and Gemini to identify which competitors get cited and why."
    },
    {
      id: "c10",
      num: "10",
      title: "Multi-Stream Measurement",
      desc: "Separate server log hits, GSC AI impressions, Bing citations, GA4 referrals, and leads into distinct tracking streams."
    }
  ];

  const completedChecksCount = Object.values(checkedChecks).filter(Boolean).length;

  const samplePrompts = [
    {
      label: "B2B Software / SaaS Prompt",
      prompt: "Which [category, e.g. HRMS / CRM software] is best for a team of 20 in India? Compare pricing, ease of payroll integration, and employee onboarding."
    },
    {
      label: "Comparison & Suitability Fan-Out",
      prompt: "What are the key differences between [Your Brand] and [Top Competitor]? What are the main limitations of each?"
    },
    {
      label: "High-Intent Buying Journey Prompt",
      prompt: "How much does [service / solution] cost on average, and what questions should I ask before hiring a provider?"
    }
  ];

  return (
    <div className="min-h-screen bg-[#030712] text-slate-100 selection:bg-cyan-500/30 selection:text-cyan-200">
      <Navbar />

      {/* Hero Header */}
      <div className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden border-b border-white/10">
        {/* Glow gradients */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[350px] bg-gradient-to-tr from-cyan-600/15 via-blue-600/20 to-purple-600/15 blur-[120px] pointer-events-none rounded-full" />
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-xs font-mono text-slate-400 mb-6">
            <Link to="/" className="hover:text-cyan-400 transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
            <Link to="/blog/" className="hover:text-cyan-400 transition-colors">Blog</Link>
            <ChevronRight className="w-3.5 h-3.5 text-slate-600" />
            <span className="text-cyan-300 truncate max-w-[220px] sm:max-w-none">AI Visibility Audit Guide</span>
          </nav>

          {/* Category & Badge */}
          <div className="flex flex-wrap items-center gap-2.5 mb-5">
            <span className="px-3 py-1 rounded-full text-xs font-mono font-bold bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
              <span>AEO/GEO , AI Visibility Playbook</span>
            </span>
            <span className="px-3 py-1 rounded-full text-xs font-mono font-bold bg-purple-500/10 text-purple-300 border border-purple-500/30 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-purple-400" />
              <span>10-Point Technical Checklist</span>
            </span>
          </div>

          {/* Title & Subtitle */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-black text-white tracking-tight leading-[1.15] mb-5 font-heading">
            How Do I Audit My Website for Better AI Visibility Across ChatGPT, Google AI Overviews and Claude?
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 leading-relaxed max-w-3xl font-normal">
            Is your website AI-ready? Discover whether artificial intelligence search engines can crawl, understand, and cite your content—and learn how to audit your site yourself using these 10 practical checks.
          </p>

          {/* Post Meta */}
          <div className="flex flex-wrap items-center gap-4 sm:gap-6 mt-8 pt-6 border-t border-white/10 text-xs sm:text-sm text-slate-400">
            <div className="flex items-center gap-2">
              <img
                src="/narendra-avatar.jpg"
                alt="Kanna Narendra Kumar"
                className="w-8 h-8 rounded-full border border-cyan-400/50 object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/narendra-hero.jpg";
                }}
              />
              <div>
                <span className="text-white font-medium">Kanna Narendra Kumar</span>
                <span className="text-slate-500 hidden sm:inline"> · Senior SEO & AI Strategist</span>
              </div>
            </div>

            <div className="flex items-center gap-1.5 font-mono text-slate-400">
              <Calendar className="w-4 h-4 text-slate-500" />
              <span>Oct 22, 2026</span>
            </div>

            <div className="flex items-center gap-1.5 font-mono text-slate-400">
              <Clock className="w-4 h-4 text-slate-500" />
              <span>12 min read</span>
            </div>

            <button
              onClick={handleCopyLink}
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 text-xs font-mono transition-colors border border-white/10"
              title="Copy article link"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-emerald-400">Link Copied!</span>
                </>
              ) : (
                <>
                  <Share2 className="w-3.5 h-3.5" />
                  <span>Share</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <article className="prose prose-invert prose-cyan max-w-none space-y-8 text-slate-300 leading-relaxed text-base sm:text-lg">

          {/* Executive Summary / Key Takeaways Box (Ideal for LLM citations & Fan-Out RAG) */}
          <div className="p-6 sm:p-7 rounded-2xl bg-gradient-to-br from-[#0c1322] to-[#070b14] border border-cyan-500/30 shadow-2xl relative overflow-hidden not-prose">
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-cyan-400 font-bold mb-3">
              <Sparkles className="w-4 h-4 text-cyan-400" />
              <span>Executive Summary &amp; Direct Takeaway</span>
            </div>
            <h3 className="text-lg sm:text-xl font-heading font-black text-white mb-3 tracking-tight">
              What Is an AI Visibility Audit and Why Do Conventional SEO Audits Fall Short?
            </h3>
            <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-4">
              An <strong>AI visibility audit</strong> tests whether AI engines—such as <strong>ChatGPT Search</strong>, <strong>Google AI Overviews</strong>, <strong>Claude</strong>, and <strong>Perplexity</strong>—can access your website, render your core answers, understand your entity relationships, and cite your brand during conversational <strong>fan-out queries</strong>. While traditional SEO audits focus on keyword ranks and backlinks, AI audits prioritize bot accessibility (<code className="text-cyan-300 font-mono text-xs">OAI-SearchBot</code>, <code className="text-cyan-300 font-mono text-xs">Claude-SearchBot</code>), zero-JS rendered HTML, information gain, and structured evidence.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm font-mono text-slate-200 pt-2 border-t border-white/10">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Crawlers: robots.txt + WAF + logs</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Zero-JS HTML parity for AI bots</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Customer journey fan-out query maps</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Multi-stream tracking (GSC + Bing + GA4)</span>
              </div>
            </div>
          </div>

          {/* Quick Table of Contents */}
          <div className="p-5 sm:p-6 rounded-2xl bg-[#090e1c] border border-white/10 not-prose">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-400 font-bold mb-3 flex items-center gap-2">
              <Compass className="w-4 h-4 text-cyan-400" />
              <span>In This AI Audit Guide (10 Checks)</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-slate-300">
              <a href="#check-1" className="hover:text-cyan-300 transition-colors flex items-center gap-2">
                <span className="text-xs font-mono text-cyan-400 font-bold">01.</span> AI Search Bot Access (robots.txt &amp; WAF)
              </a>
              <a href="#check-2" className="hover:text-cyan-300 transition-colors flex items-center gap-2">
                <span className="text-xs font-mono text-cyan-400 font-bold">02.</span> Important Page Indexability
              </a>
              <a href="#check-3" className="hover:text-cyan-300 transition-colors flex items-center gap-2">
                <span className="text-xs font-mono text-cyan-400 font-bold">03.</span> JavaScript Rendering &amp; DOM Parity
              </a>
              <a href="#check-4" className="hover:text-cyan-300 transition-colors flex items-center gap-2">
                <span className="text-xs font-mono text-cyan-400 font-bold">04.</span> Customer Buying Questions &amp; Fan-Outs
              </a>
              <a href="#check-5" className="hover:text-cyan-300 transition-colors flex items-center gap-2">
                <span className="text-xs font-mono text-cyan-400 font-bold">05.</span> Content Citability &amp; Structure (AEO)
              </a>
              <a href="#check-6" className="hover:text-cyan-300 transition-colors flex items-center gap-2">
                <span className="text-xs font-mono text-cyan-400 font-bold">06.</span> Canonicals, Redirects &amp; Internal Links
              </a>
              <a href="#check-7" className="hover:text-cyan-300 transition-colors flex items-center gap-2">
                <span className="text-xs font-mono text-cyan-400 font-bold">07.</span> Business Entity &amp; Schema Understanding
              </a>
              <a href="#check-8" className="hover:text-cyan-300 transition-colors flex items-center gap-2">
                <span className="text-xs font-mono text-cyan-400 font-bold">08.</span> Verifiable Evidence &amp; E-E-A-T Claims
              </a>
              <a href="#check-9" className="hover:text-cyan-300 transition-colors flex items-center gap-2">
                <span className="text-xs font-mono text-cyan-400 font-bold">09.</span> Competitor AI SERP Audit &amp; Citations
              </a>
              <a href="#check-10" className="hover:text-cyan-300 transition-colors flex items-center gap-2">
                <span className="text-xs font-mono text-cyan-400 font-bold">10.</span> Multi-Stream Visibility Measurement
              </a>
            </div>
          </div>

          {/* Section: Is Your Website AI-Ready? */}
          <section className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-heading font-black text-white tracking-tight">
              Is Your Website AI-Ready? Audit It Yourself With These 10 Checks
            </h2>

            <p>
              Is your website ready to be discovered, understood, and cited by AI platforms such as <strong>ChatGPT</strong>, <strong>Google AI Overviews</strong>, <strong>Claude</strong>, and <strong>Perplexity</strong>?
            </p>

            <p>
              You can find out by auditing your website yourself.
            </p>

            <p>
              Several factors determine how AI search engines access your website, digest your content, and identify trustworthy information to synthesize into their answers. These include technical accessibility, indexability, content quality, business entity clarity, and the verifiable evidence supporting your claims.
            </p>

            <p>
              In this comprehensive guide, you&apos;ll learn how to perform <strong>10 vital AI visibility audit checks</strong> on your own website. Each section explains what to check, why it matters, how to spot hidden blockers, and exactly how to fix them.
            </p>

            <p>
              You don&apos;t need to be an enterprise technical SEO engineer to run these checks. Some require free tools like Google Search Console, a browser developer console, or server logs, while others involve reviewing your website content and testing customer questions across AI platforms.
            </p>

            <p>
              By the end of this guide, you&apos;ll have a practical checklist to identify barriers to AI visibility, prioritize fixes, and evaluate whether your website is becoming easier for AI search systems to discover and cite.
            </p>

            {/* Strategic Internal Links Callout */}
            <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10 text-sm text-slate-300 flex items-start gap-3 my-4">
              <Layers className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
              <div>
                <span className="font-bold text-white">Recommended Reading:</span> Before diving in, explore our foundational deep dive on{" "}
                <Link to="/blog/can-ai-search-engines-crawl-understand-cite-website/" className="text-cyan-400 underline hover:text-cyan-300 font-semibold">
                  Can AI Search Engines Crawl, Understand and Cite Your Website?
                </Link>{" "}
                and understand why high rankings don&apos;t automatically transfer to AI answers in{" "}
                <Link to="/blog/why-do-i-rank-on-google-but-dont-appear-in-chatgpt/" className="text-cyan-400 underline hover:text-cyan-300 font-semibold">
                  Why Do I Rank on Google but Don&apos;t Appear in ChatGPT?
                </Link>
              </div>
            </div>
          </section>


          <p className="text-slate-300">
            Let&apos;s start with the technical accessibility checks before moving to content quality, customer questions, and competitive AI SERP analysis.
          </p>

          <hr className="border-white/10 my-8" />

          {/* CHECK 1 */}
          <section id="check-1" className="space-y-4 scroll-mt-24">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/30 font-mono font-bold text-cyan-300 text-sm flex items-center justify-center">
                01
              </span>
              <h2 className="text-2xl sm:text-3xl font-heading font-black text-white tracking-tight m-0">
                Check Whether AI Search Bots Can Access Your Website
              </h2>
            </div>

            <p>
              Before improving your content, check whether the relevant AI search systems can actually access it.
            </p>

            <p>
              Some AI services use crawlers to discover content for live search results and AI-generated answers. Others use crawlers for distinct purposes, such as collecting bulk pre-training data.
            </p>

            <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-cyan-400 text-white font-medium not-italic my-4">
              <strong>Crucial Rule:</strong> Allowing one type of bot does not automatically allow every bot associated with that service. For instance, allowing OpenAI&apos;s <code>OAI-SearchBot</code> is required for ChatGPT Search, whereas blocking <code>GPTBot</code> stops training data scraping while keeping search discovery active.
            </blockquote>

            <h3 className="text-xl font-heading font-bold text-white mt-6">What should you check?</h3>

            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10">
                <h4 className="font-bold text-cyan-300 text-base mb-1 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> 1. Check your robots.txt file
                </h4>
                <p className="text-sm text-slate-300 mb-2">
                  Open <code>yourdomain.com/robots.txt</code> and review the rules that apply to relevant search crawlers:
                </p>
                <ul className="text-xs sm:text-sm font-mono text-slate-300 space-y-1.5 pl-4 list-disc">
                  <li><strong>OAI-SearchBot</strong>: OpenAI&apos;s crawler for ChatGPT search results.</li>
                  <li><strong>Googlebot</strong>: Used for Google Search and eligible Google AI search features (like AI Overviews).</li>
                  <li><strong>Claude-SearchBot</strong>: Used for search-related discovery and citations by Anthropic.</li>
                  <li><strong>PerplexityBot</strong>: The retrieval crawler for Perplexity search answers.</li>
                </ul>
                <div className="p-3 rounded-lg bg-[#070b14] border border-white/10 font-mono text-xs text-slate-300 mt-3 overflow-x-auto">
                  <span className="text-slate-500"># Example of clean, unblocked AI search crawler configuration:</span><br />
                  <span className="text-cyan-400">User-agent:</span> OAI-SearchBot<br />
                  <span className="text-emerald-400">Allow:</span> /<br /><br />
                  <span className="text-cyan-400">User-agent:</span> Claude-SearchBot<br />
                  <span className="text-emerald-400">Allow:</span> /<br /><br />
                  <span className="text-cyan-400">User-agent:</span> PerplexityBot<br />
                  <span className="text-emerald-400">Allow:</span> /
                </div>
              </div>

              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10">
                <h4 className="font-bold text-cyan-300 text-base mb-1 flex items-center gap-2">
                  <Lock className="w-4 h-4" /> 2. Check your website security settings &amp; WAF
                </h4>
                <p className="text-sm text-slate-300 mb-2">
                  Even when <code>robots.txt</code> allows a crawler, your CDN (Cloudflare, AWS CloudFront, Fastly), firewall, or hosting security can silently drop requests. Look for:
                </p>
                <ul className="text-sm text-slate-300 space-y-1 pl-4 list-disc">
                  <li><strong>HTTP 403 Forbidden:</strong> The crawler is explicitly denied access by security rules.</li>
                  <li><strong>HTTP 429 Too Many Requests:</strong> Aggressive rate limiting blocks automated fetches.</li>
                  <li><strong>CAPTCHA &amp; Cloudflare Turnstile Challenges:</strong> Headless bots cannot solve interactive challenges and will fail.</li>
                  <li><strong>Bot mitigation filters:</strong> Security modules that flag AI crawlers as generic scrapers.</li>
                </ul>
              </div>

              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10">
                <h4 className="font-bold text-cyan-300 text-base mb-1 flex items-center gap-2">
                  <Activity className="w-4 h-4" /> 3. Check your server logs
                </h4>
                <p className="text-sm text-slate-300">
                  Server logs provide undeniable evidence of which bots visit your website, which URLs they request, and whether those requests succeed (returning <code>200 OK</code>). If you don&apos;t see visits, verify whether logs cover a sufficient timeframe before assuming a block.
                </p>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-cyan-950/20 border border-cyan-500/30 text-sm text-slate-300">
              <strong className="text-white">What should you fix?</strong> Correct unintended crawler disallows, update firewall rules to whitelist genuine search bots, and verify that important pages return HTTP 200 to AI User-Agents.
            </div>

            <p className="text-xs sm:text-sm text-slate-400 italic">
              <strong>Why this matters:</strong> Removing confirmed access barriers makes your content available to AI retrieval systems. It does not guarantee a citation, but blocking them guarantees invisibility.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* CHECK 2 */}
          <section id="check-2" className="space-y-4 scroll-mt-24">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/30 font-mono font-bold text-cyan-300 text-sm flex items-center justify-center">
                02
              </span>
              <h2 className="text-2xl sm:text-3xl font-heading font-black text-white tracking-tight m-0">
                Check Whether Your Important Pages Are Indexed
              </h2>
            </div>

            <p>
              A page can exist on your server without being in Google&apos;s index.
            </p>

            <p>
              If a page is not indexed, it generally cannot appear in Google&apos;s conventional search results or its <strong>Google AI Overviews</strong> while it remains unindexed.
            </p>

            <p>
              Other AI platforms may use different discovery and retrieval systems, so Google indexing alone does not tell you everything about their access—but for Google AI features, it is a non-negotiable prerequisite.
            </p>

            <h3 className="text-xl font-heading font-bold text-white mt-6">What should you check?</h3>

            <p>Use Google Search Console (GSC) to review the indexing status of your highest-value assets, including:</p>
            <ul className="list-disc pl-6 space-y-1.5 text-slate-300">
              <li>Product and service pages</li>
              <li>Pricing and plan tier pages</li>
              <li>Direct comparison pages (e.g. &ldquo;Us vs Competitor&rdquo;)</li>
              <li>Case studies and customer evidence pages (such as our{" "}
                <Link to="/casestudy/seismic/" className="text-cyan-400 underline hover:text-cyan-300">Seismic B2B SaaS case study</Link> or{" "}
                <Link to="/casestudy/myoperator/" className="text-cyan-400 underline hover:text-cyan-300">MyOperator enterprise scale</Link>)
              </li>
              <li>Key informational answer articles</li>
            </ul>

            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-2">
              <div className="text-sm font-bold text-cyan-300">Common indexability problems to audit:</div>
              <ul className="text-xs sm:text-sm text-slate-300 space-y-1 pl-4 list-disc">
                <li>Pages accidentally marked with <code>&lt;meta name="robots" content="noindex"&gt;</code>.</li>
                <li>URLs blocked by <code>robots.txt</code> that prevent Googlebot from re-crawling.</li>
                <li>Mismatched or erroneous canonical tags pointing away from the true URL.</li>
                <li>Orphan pages with zero internal links pointing to them.</li>
                <li>URLs completely missing from your <code>sitemap.xml</code>.</li>
              </ul>
            </div>

            <div className="p-4 rounded-xl bg-cyan-950/20 border border-cyan-500/30 text-sm text-slate-300">
              <strong className="text-white">What should you fix?</strong> Identify why each priority page is excluded in GSC, fix confirmed directive or structural errors, and request re-indexing. Avoid indexing thin or duplicate parameter URLs.
            </div>

            <p className="text-xs sm:text-sm text-slate-400 italic">
              <strong>Why this matters:</strong> Indexing is an eligibility baseline for Google AI search experiences. Without it, you cannot be synthesized into Google AI Overviews.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* CHECK 3 */}
          <section id="check-3" className="space-y-4 scroll-mt-24">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/30 font-mono font-bold text-cyan-300 text-sm flex items-center justify-center">
                03
              </span>
              <h2 className="text-2xl sm:text-3xl font-heading font-black text-white tracking-tight m-0">
                Check Whether Your Website Content Is Accessible Without JavaScript Problems
              </h2>
            </div>

            <p>
              Your website might look stunning in a desktop browser while crucial information is completely missing from the original HTML received by crawlers.
            </p>

            <p>
              This frequently happens when product descriptions, pricing matrices, specifications, or FAQ answers are hydrated exclusively through client-side JavaScript (React, Next.js client components, Angular, or Vue).
            </p>

            <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-amber-400 text-white font-medium not-italic my-4">
              <strong>The AI Crawling Reality:</strong> While Googlebot has a rendering queue, many real-time AI retrieval agents (such as RAG scrapers and fast answer bots) do <em>not</em> run full headless browser rendering pipelines due to latency constraints. If your content is not in the raw HTML, AI retrieval bots may only see an empty shell.
            </blockquote>

            <h3 className="text-xl font-heading font-bold text-white mt-6">What should you check?</h3>

            <p>
              Pick a sample of important pages and compare the raw HTML (via browser <strong>View Page Source</strong> or <code>curl</code>) against the fully rendered DOM in Developer Tools:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="p-3.5 rounded-xl bg-white/[0.02] border border-white/10 space-y-1">
                <span className="text-xs font-mono font-bold text-cyan-400">Inspect In Raw HTML:</span>
                <p className="text-xs text-slate-300">Are main H1/H2 headings, pricing numbers, and feature bullet points present?</p>
              </div>
              <div className="p-3.5 rounded-xl bg-white/[0.02] border border-white/10 space-y-1">
                <span className="text-xs font-mono font-bold text-cyan-400">Accordion &amp; Tab State:</span>
                <p className="text-xs text-slate-300">Is text hidden until user click? Ensure text is in the DOM rather than fetched on demand.</p>
              </div>
              <div className="p-3.5 rounded-xl bg-white/[0.02] border border-white/10 space-y-1">
                <span className="text-xs font-mono font-bold text-cyan-400">Comparison Tables:</span>
                <p className="text-xs text-slate-300">Do competitor comparison tables render as semantic HTML <code>&lt;table&gt;</code> elements?</p>
              </div>
              <div className="p-3.5 rounded-xl bg-white/[0.02] border border-white/10 space-y-1">
                <span className="text-xs font-mono font-bold text-cyan-400">API Hydration Delays:</span>
                <p className="text-xs text-slate-300">Does content fail to load if JavaScript execution is disabled in DevTools?</p>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-cyan-950/20 border border-cyan-500/30 text-sm text-slate-300">
              <strong className="text-white">What should you fix?</strong> Implement server-side rendering (SSR), Static Site Generation (SSG), or pre-rendering for all critical content.
            </div>

            <p className="text-xs sm:text-sm text-slate-400 italic">
              <strong>Why this matters:</strong> Making important content reliably accessible in raw HTML removes friction for AI retrieval engines.
            </p>
          </section>

          {/* Mid-Article AI Visibility Audit CTA Callout (~30% Scroll) */}
          <div className="my-8 p-6 sm:p-7 rounded-2xl bg-gradient-to-r from-[#0c1629] via-[#0f1d38] to-[#0c1426] border border-cyan-500/35 shadow-xl shadow-cyan-950/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5 not-prose">
            <div className="space-y-1.5">
              <div className="inline-flex items-center gap-1.5 text-[11px] font-mono font-bold text-cyan-400 uppercase tracking-wider">
                <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                <span>Done-For-You 4-Layer Diagnostic</span>
              </div>
              <h3 className="font-heading font-bold text-lg sm:text-xl text-white leading-snug">
                Want Us to Run All 10 AI Visibility Checks on Your Website?
              </h3>
              <p className="text-xs sm:text-sm text-slate-300 max-w-xl leading-relaxed">
                Get a complete 72-hour AI Visibility Audit covering bot access, zero-JS HTML rendering, customer fan-out topics, and 1-on-1 strategy walkthroughs.
              </p>
            </div>
            <Link
              to="/ai-visibility-audit/"
              className="shrink-0 inline-flex items-center gap-2 px-5 py-3 rounded-xl btn-bold-primary text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-cyan-500/25 hover:scale-[1.02] transition-all"
            >
              <span>Get an AI Visibility Audit</span>
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          <hr className="border-white/10 my-8" />

          {/* CHECK 4 */}
          <section id="check-4" className="space-y-4 scroll-mt-24">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/30 font-mono font-bold text-cyan-300 text-sm flex items-center justify-center">
                04
              </span>
              <h2 className="text-2xl sm:text-3xl font-heading font-black text-white tracking-tight m-0">
                Check Whether Your Content Answers Real Customer Questions
              </h2>
            </div>

            <p>
              Technical accessibility is only one half of an AI visibility audit.
            </p>

            <p>
              Your website also requires rich, useful content that directly answers the multi-stage questions customers ask before purchasing a product or service.
            </p>

            <p>
              A website can have hundreds of generalized blog articles and still fail to answer the specific questions buyers ask AI systems.
            </p>

            <h3 className="text-xl font-heading font-bold text-white mt-6">Understanding Customer Buying Journeys &amp; Fan-Out Queries</h3>

            <p>
              When a user asks an AI engine a question, the AI rarely provides a simple list of keywords. Instead, it unpacks the intent into <strong>fan-out queries</strong> representing different decision stages:
            </p>

            <div className="p-5 rounded-2xl bg-[#090e1c] border border-cyan-500/20 space-y-3 font-mono text-xs sm:text-sm">
              <div className="text-cyan-400 font-bold uppercase tracking-wider text-[11px]">
                Example: B2B HR Software Customer Journey Fan-Outs
              </div>
              <div className="space-y-2 text-slate-300">
                <div className="flex items-start gap-2">
                  <span className="text-cyan-400 font-bold shrink-0">1. Problem:</span>
                  <span>&ldquo;When does a 30-person company outgrow spreadsheets for payroll?&rdquo;</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-cyan-400 font-bold shrink-0">2. Solution:</span>
                  <span>&ldquo;Which HR software is suitable for a small business in India?&rdquo;</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-cyan-400 font-bold shrink-0">3. Comparison:</span>
                  <span>&ldquo;What should I compare between [Tool A] vs [Tool B] regarding compliance?&rdquo;</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-cyan-400 font-bold shrink-0">4. Pricing:</span>
                  <span>&ldquo;How much does HR software cost per employee per month in India?&rdquo;</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-cyan-400 font-bold shrink-0">5. Risk &amp; Setup:</span>
                  <span>&ldquo;How difficult is it to migrate historical employee records into an HRMS?&rdquo;</span>
                </div>
              </div>
            </div>

            <h4 className="font-bold text-white text-lg mt-4">How to Audit Your Existing Content Gaps:</h4>
            <p>
              List your top 20 customer questions and map each against your website pages. Classify each question into one of three tiers:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs sm:text-sm">
              <div className="p-4 rounded-xl bg-emerald-950/20 border border-emerald-500/30 space-y-1">
                <div className="font-bold text-emerald-400 flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4" /> Fully Covered
                </div>
                <p className="text-slate-300">
                  An existing page provides a clear, complete, and evidence-backed answer with specifications.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-amber-950/20 border border-amber-500/30 space-y-1">
                <div className="font-bold text-amber-400 flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4" /> Partially Covered
                </div>
                <p className="text-slate-300">
                  The page touches the topic broadly but misses pricing numbers, limitations, or comparisons.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-rose-950/20 border border-rose-500/30 space-y-1">
                <div className="font-bold text-rose-400 flex items-center gap-1.5">
                  <HelpCircle className="w-4 h-4" /> Not Covered
                </div>
                <p className="text-slate-300">
                  No page answers the question, forcing AI models to quote competitors instead.
                </p>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-cyan-950/20 border border-cyan-500/30 text-sm text-slate-300">
              <strong className="text-white">What should you fix?</strong> Upgrade partially covered pages with direct facts and data. Group tightly related fan-out questions into authoritative pillar pages rather than scattering thin posts.
            </div>

            <p className="text-xs sm:text-sm text-slate-400 italic">
              <strong>Why this matters:</strong> Comprehensive question coverage ensures your brand is retrieved whenever AI engines synthesize multi-part buying advice.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* CHECK 5 */}
          <section id="check-5" className="space-y-4 scroll-mt-24">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/30 font-mono font-bold text-cyan-300 text-sm flex items-center justify-center">
                05
              </span>
              <h2 className="text-2xl sm:text-3xl font-heading font-black text-white tracking-tight m-0">
                Check Whether Your Content Is Easy to Understand and Cite
              </h2>
            </div>

            <p>
              Even when a page is technically accessible, its content formatting can prevent an AI model from extracting a clean, authoritative quote.
            </p>

            <p>
              If a reader (or an AI embedding model) has to parse through four paragraphs of generic fluff before reaching the core answer, the passage score drops and retrieval confidence suffers.
            </p>

            <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-cyan-400 text-white font-medium not-italic my-4">
              <strong>Answer Engine Optimization (AEO) Principle:</strong> Provide the direct answer first (inverted pyramid style), followed by supporting data, nuances, and real-world examples.
            </blockquote>

            <h3 className="text-xl font-heading font-bold text-white mt-6">Audit Questions for Citability:</h3>

            <div className="space-y-2 text-sm text-slate-300">
              <div className="p-3 rounded-xl bg-white/[0.02] border border-white/10 flex items-start gap-2.5">
                <Check className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                <span><strong>Directness:</strong> Does the first sentence beneath an H2 directly answer the question without boilerplate preamble?</span>
              </div>
              <div className="p-3 rounded-xl bg-white/[0.02] border border-white/10 flex items-start gap-2.5">
                <Check className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                <span><strong>Descriptive Headings:</strong> Are headings explicit and semantic (e.g. &ldquo;How Much Does HRMS Cost in India?&rdquo; instead of &ldquo;Pricing Overview&rdquo;)?</span>
              </div>
              <div className="p-3 rounded-xl bg-white/[0.02] border border-white/10 flex items-start gap-2.5">
                <Check className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                <span><strong>Structured Tables:</strong> Are feature comparisons, plans, and technical specifications organized in clean HTML tables?</span>
              </div>
              <div className="p-3 rounded-xl bg-white/[0.02] border border-white/10 flex items-start gap-2.5">
                <Check className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                <span><strong>Specificity:</strong> Do you avoid empty marketing adjectives like &ldquo;the best solution for everyone&rdquo; in favor of factual parameters?</span>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-cyan-950/20 border border-cyan-500/30 text-sm text-slate-300">
              <strong className="text-white">What should you fix?</strong> Rewrite introductory sections to deliver atomic answers upfront, add comparison tables for complex features, and structure chronological processes as ordered lists.
            </div>

            <p className="text-xs sm:text-sm text-slate-400 italic">
              <strong>Why this matters:</strong> Clear, modular content allows AI retrieval systems to isolate relevant text chunks with high semantic similarity scores.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* CHECK 6 */}
          <section id="check-6" className="space-y-4 scroll-mt-24">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/30 font-mono font-bold text-cyan-300 text-sm flex items-center justify-center">
                06
              </span>
              <h2 className="text-2xl sm:text-3xl font-heading font-black text-white tracking-tight m-0">
                Check Your Canonical Tags, Redirects and Internal Links
              </h2>
            </div>

            <p>
              Search engines and AI discovery systems must understand which URLs represent the intended, authoritative versions of your pages.
            </p>

            <p>
              Technical inconsistencies dilute PageRank, confuse crawlers, and cause AI systems to ignore updated pages in favor of outdated cached copies.
            </p>

            <h3 className="text-xl font-heading font-bold text-white mt-6">What should you check?</h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs sm:text-sm">
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-2">
                <span className="font-bold text-cyan-300 font-mono text-xs uppercase tracking-wider">Canonicals</span>
                <p className="text-slate-300">
                  Ensure every unique page has a valid self-referencing canonical tag. Watch out for tags pointing mistakenly to the homepage, to 404 URLs, or to different language variants.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-2">
                <span className="font-bold text-cyan-300 font-mono text-xs uppercase tracking-wider">Redirect Chains</span>
                <p className="text-slate-300">
                  Audit internal links to ensure they point directly to the final 200 OK destination without hopping through 301/302 redirects or HTTP-to-HTTPS hops.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-2">
                <span className="font-bold text-cyan-300 font-mono text-xs uppercase tracking-wider">Internal Linking</span>
                <p className="text-slate-300">
                  Connect top-of-funnel informational blog posts to high-intent commercial landing pages (such as linking from an audit checklist to an{" "}
                  <Link to="/ai-visibility-audit/" className="text-cyan-400 underline hover:text-cyan-300">AI visibility audit service</Link>).
                </p>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-cyan-950/20 border border-cyan-500/30 text-sm text-slate-300">
              <strong className="text-white">What should you fix?</strong> Eliminate redirect hops, correct faulty canonical directives, and ensure key commercial pages receive anchor-rich internal links from supporting articles.
            </div>

            <p className="text-xs sm:text-sm text-slate-400 italic">
              <strong>Why this matters:</strong> Clean internal linking and canonical hygiene establish clear entity hierarchy and topical authority.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* CHECK 7 */}
          <section id="check-7" className="space-y-4 scroll-mt-24">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/30 font-mono font-bold text-cyan-300 text-sm flex items-center justify-center">
                07
              </span>
              <h2 className="text-2xl sm:text-3xl font-heading font-black text-white tracking-tight m-0">
                Check Whether Search Engines Can Understand Your Business
              </h2>
            </div>

            <p>
              Your website must establish an unambiguous corporate entity: who you are, what exact products or services you offer, and which specific customer profiles you serve.
            </p>

            <p>
              This is especially crucial when your brand or product name contains common words that AI systems could confuse with other businesses or concepts.
            </p>

            <h3 className="text-xl font-heading font-bold text-white mt-6">Entity Clarity Audit Points:</h3>

            <div className="space-y-2.5 text-sm text-slate-300">
              <div className="p-3.5 rounded-xl bg-white/[0.02] border border-white/10">
                <strong className="text-cyan-300">Dedicated About &amp; Contact Pages:</strong> Clear corporate address, registration, founding date, leadership team, and contact channels.
              </div>
              <div className="p-3.5 rounded-xl bg-white/[0.02] border border-white/10">
                <strong className="text-cyan-300">Author Bios &amp; Credentials:</strong> Real human authors with verifiable background, LinkedIn profile links, and topical experience.
              </div>
              <div className="p-3.5 rounded-xl bg-white/[0.02] border border-white/10">
                <strong className="text-cyan-300">Structured Data (JSON-LD Schema):</strong> Implement valid <code>Organization</code>, <code>Person</code>, <code>Product</code>, <code>TechArticle</code>, and <code>FAQPage</code> schemas with matching <code>sameAs</code> social citations.
              </div>
            </div>

            <div className="p-4 rounded-xl bg-cyan-950/20 border border-cyan-500/30 text-sm text-slate-300">
              <strong className="text-white">What should you fix?</strong> Standardize your business naming across your domain and external directories, fix schema syntax errors, and ensure schema attributes strictly match visible on-page text.
            </div>

            <p className="text-xs sm:text-sm text-slate-400 italic">
              <strong>Why this matters:</strong> Entity clarity anchors your brand in the knowledge graphs that AI models consult when validating entity facts.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* CHECK 8 */}
          <section id="check-8" className="space-y-4 scroll-mt-24">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/30 font-mono font-bold text-cyan-300 text-sm flex items-center justify-center">
                08
              </span>
              <h2 className="text-2xl sm:text-3xl font-heading font-black text-white tracking-tight m-0">
                Check Whether Your Claims Are Supported by Evidence
              </h2>
            </div>

            <p>
              Your website might describe its products eloquently but provide zero verifiable evidence to substantiate its claims.
            </p>

            <p>
              For example, claiming that your software &ldquo;boosts team productivity by 40%&rdquo; without showing sample sizes, study methodologies, or customer case studies looks like unsubstantiated marketing hype to both discerning buyers and AI evaluation systems.
            </p>

            <h3 className="text-xl font-heading font-bold text-white mt-6">What evidence to audit:</h3>

            <div className="space-y-2 text-sm text-slate-300">
              <div className="p-3 rounded-xl bg-white/[0.02] border border-white/10 flex items-start gap-2.5">
                <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                <span><strong>Customer Case Studies:</strong> Real-world implementations with client logos, baseline metrics, timeframes, and outcomes (see our real-world{" "}
                  <Link to="/casestudy/workhours/" className="text-cyan-400 underline hover:text-cyan-300">Workhours organic scaling case study</Link>).
                </span>
              </div>
              <div className="p-3 rounded-xl bg-white/[0.02] border border-white/10 flex items-start gap-2.5">
                <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                <span><strong>Original Research &amp; Statistics:</strong> Proprietary data benchmarks, industry surveys, or unique experimental data.</span>
              </div>
              <div className="p-3 rounded-xl bg-white/[0.02] border border-white/10 flex items-start gap-2.5">
                <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                <span><strong>Transparent Limitations:</strong> Acknowledging who your product is <em>not</em> suitable for builds immense credibility and prevents AI hallucinations.</span>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-cyan-950/20 border border-cyan-500/30 text-sm text-slate-300">
              <strong className="text-white">What should you fix?</strong> Replace vague claims with quantifiable case studies, link to reliable secondary citations where appropriate, and explain the methodology behind reported results.
            </div>

            <p className="text-xs sm:text-sm text-slate-400 italic">
              <strong>Why this matters:</strong> Solid evidence fuels information gain and provides the factual citations LLMs require to substantiate recommendations.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* CHECK 9 */}
          <section id="check-9" className="space-y-4 scroll-mt-24">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/30 font-mono font-bold text-cyan-300 text-sm flex items-center justify-center">
                09
              </span>
              <h2 className="text-2xl sm:text-3xl font-heading font-black text-white tracking-tight m-0">
                Check Which Competitors AI Platforms Mention Instead of You
              </h2>
            </div>

            <p>
              Often the fastest way to understand your AI visibility gap is to examine the actual answers AI platforms generate for your customers&apos; core questions.
            </p>

            <p>
              Do not rely on a single prompt or one test. Conduct structured prompt audits across ChatGPT Search, Google AI Overviews, Claude, and Perplexity.
            </p>

            <h3 className="text-xl font-heading font-bold text-white mt-6">Repeatable AI Prompt Audit Framework:</h3>

            <p className="text-sm text-slate-300">
              Test these 3 sample fan-out prompt archetypes in private/incognito windows across multiple AI platforms:
            </p>

            <div className="space-y-3 not-prose my-4">
              {samplePrompts.map((p, idx) => (
                <div key={idx} className="p-4 rounded-xl bg-[#090e1c] border border-white/10 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-mono font-bold text-cyan-400 uppercase tracking-wider">{p.label}</span>
                    <button
                      onClick={() => handleCopyPrompt(p.prompt, idx)}
                      className="inline-flex items-center gap-1 text-[11px] font-mono text-slate-400 hover:text-cyan-300 transition-colors"
                    >
                      {copiedPromptIndex === idx ? (
                        <>
                          <Check className="w-3.5 h-3.5 text-emerald-400" />
                          <span className="text-emerald-400">Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" />
                          <span>Copy Prompt</span>
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-xs sm:text-sm font-mono text-slate-200 bg-[#040711] p-2.5 rounded-lg border border-white/5">
                    {p.prompt}
                  </p>
                </div>
              ))}
            </div>

            <h4 className="font-bold text-white text-base mt-4">For each prompt, document:</h4>
            <ul className="list-disc pl-6 space-y-1 text-sm text-slate-300">
              <li>Does your brand appear in the recommendation list?</li>
              <li>Which competitors are highlighted and which specific features are praised?</li>
              <li>Which specific URLs are cited in the footnote or pill links?</li>
              <li>Does the AI response reveal a content or data gap that your website currently lacks?</li>
            </ul>

            <div className="p-4 rounded-xl bg-cyan-950/20 border border-cyan-500/30 text-sm text-slate-300">
              <strong className="text-white">What should you fix?</strong> Inspect competitors&apos; cited pages to discover why the AI preferred them: did they provide clearer pricing? Better comparison tables? Stronger author credentials? Use these insights to upgrade your own assets. To systematically map these customer objections, questions, and buying decisions, follow our guide on{" "}
              <Link
                to="/blog/how-to-do-keyword-research-for-ai-visibility/"
                className="text-cyan-400 hover:text-cyan-300 underline font-semibold"
              >
                how to do keyword research for AI visibility
              </Link>
              .
            </div>

            <p className="text-xs sm:text-sm text-slate-400 italic">
              <strong>Why this matters:</strong> Competitive AI SERP audits reveal real-world citation criteria that traditional keyword crawlers overlook.
            </p>
          </section>

          <hr className="border-white/10 my-8" />

          {/* CHECK 10 */}
          <section id="check-10" className="space-y-4 scroll-mt-24">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/30 font-mono font-bold text-cyan-300 text-sm flex items-center justify-center">
                10
              </span>
              <h2 className="text-2xl sm:text-3xl font-heading font-black text-white tracking-tight m-0">
                Measure AI Visibility Before and After Making Changes
              </h2>
            </div>

            <p>
              An audit is only effective if you can measure whether your optimizations are generating real-world business results.
            </p>

            <p>
              <strong>Do not rely on organic website traffic alone as your metric for AI search.</strong> Many AI interactions result in zero-click synthesis where the user receives their answer directly within the conversational interface.
            </p>

            <h3 className="text-xl font-heading font-bold text-white mt-6">Track 5 Separate Measurement Streams:</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm">
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-1">
                <span className="font-bold text-cyan-300 font-mono text-xs uppercase">1. AI Bot Requests (Server Logs)</span>
                <p className="text-slate-300">Count weekly 200 OK hits from OAI-SearchBot, Claude-SearchBot, and PerplexityBot.</p>
              </div>
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-1">
                <span className="font-bold text-cyan-300 font-mono text-xs uppercase">2. Google AI Impressions (GSC)</span>
                <p className="text-slate-300">Monitor Generative AI search impressions in Google Search Console.</p>
              </div>
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-1">
                <span className="font-bold text-cyan-300 font-mono text-xs uppercase">3. Bing Copilot Citations</span>
                <p className="text-slate-300">Track AI citation counts reported in Bing Webmaster Tools.</p>
              </div>
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-1">
                <span className="font-bold text-cyan-300 font-mono text-xs uppercase">4. GA4 AI Referral Traffic</span>
                <p className="text-slate-300">Isolate referral sessions from chatgpt.com, claude.ai, and perplexity.ai.</p>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-1">
              <span className="font-bold text-cyan-300 font-mono text-xs uppercase">5. Down-Funnel Business Outcomes</span>
              <p className="text-sm text-slate-300">
                Track qualified inquiries, demo bookings, and phone calls originating from AI visitors. AI traffic often converts at significantly higher rates because the user has already conducted thorough research before clicking.
              </p>
            </div>

            <p className="text-sm text-slate-300">
              For a detailed step-by-step spreadsheet framework to set this up yourself without expensive tools, read our comprehensive guide:{" "}
              <Link to="/blog/how-to-measure-ai-search-visibility-for-free/" className="text-cyan-400 underline hover:text-cyan-300 font-semibold">
                How to Measure AI Search Visibility for Free (Without Paid Tools)
              </Link>.
            </p>

            <div className="p-4 rounded-xl bg-cyan-950/20 border border-cyan-500/30 text-sm text-slate-300">
              <strong className="text-white">What should you fix?</strong> Record baseline metrics before publishing fixes, note the date of technical and content changes, and measure again after a 6-to-8 week evaluation window.
            </div>

            <p className="text-xs sm:text-sm text-slate-400 italic">
              <strong>Why this matters:</strong> Clear multi-stream tracking separates completed website improvements from actual shifts in citations and inbound revenue.
            </p>
          </section>

          <hr className="border-white/10 my-10" />

          {/* INTERACTIVE 10-CHECK AUDIT CHECKLIST COMPONENT */}
          <div className="p-6 sm:p-8 rounded-3xl bg-[#090e1c] border border-cyan-500/30 shadow-2xl not-prose my-10">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-white/10">
              <div>
                <span className="text-xs font-mono font-bold text-cyan-400 uppercase tracking-widest flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400" />
                  <span>Interactive Audit Tool</span>
                </span>
                <h3 className="text-xl sm:text-2xl font-heading font-black text-white tracking-tight mt-1">
                  Your 10-Point AI Visibility Audit Checklist
                </h3>
                <p className="text-xs sm:text-sm text-slate-400 mt-1">
                  Click each check as you complete your website evaluation:
                </p>
              </div>

              <div className="text-left sm:text-right shrink-0">
                <span className="text-xs font-mono text-cyan-400 font-bold">
                  {completedChecksCount} of {auditChecklistItems.length} Completed ({Math.round((completedChecksCount / auditChecklistItems.length) * 100)}%)
                </span>
                <div className="w-40 h-2.5 rounded-full bg-white/10 mt-1.5 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-cyan-400 to-indigo-500 transition-all duration-300"
                    style={{ width: `${(completedChecksCount / auditChecklistItems.length) * 100}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 mt-6">
              {auditChecklistItems.map((item) => {
                const isChecked = !!checkedChecks[item.id];
                return (
                  <div
                    key={item.id}
                    onClick={() => toggleCheck(item.id)}
                    className={`p-4 rounded-2xl border transition-all cursor-pointer select-none text-left flex items-start gap-3.5 ${
                      isChecked
                        ? "bg-cyan-950/30 border-cyan-500/50 text-white shadow-lg shadow-cyan-950/30"
                        : "bg-white/[0.03] border-white/10 text-white/70 hover:border-white/20 hover:bg-white/[0.05]"
                    }`}
                  >
                    <div className="mt-0.5 shrink-0 text-cyan-400">
                      {isChecked ? (
                        <CheckSquare className="w-5 h-5 text-cyan-400" />
                      ) : (
                        <Square className="w-5 h-5 text-white/40" />
                      )}
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-mono text-cyan-400 font-bold">
                          {item.num}
                        </span>
                        <p className={`font-heading font-bold text-sm ${isChecked ? "text-cyan-300" : "text-white"}`}>
                          {item.title}
                        </p>
                      </div>
                      <p className="text-xs text-slate-300 leading-relaxed font-normal">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 pt-5 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs sm:text-sm text-slate-300">
                Prefer a complete done-for-you 10-point AI Visibility Audit with prioritized engineering &amp; content fixes?
              </p>
              <Link
                to="/ai-visibility-audit/"
                className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-400/40 text-cyan-300 hover:text-white text-xs font-mono font-bold uppercase tracking-wider transition-all"
              >
                <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
                <span>Book Your AI Visibility Audit</span>
                <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>

          {/* COMPREHENSIVE COMPARISON MATRIX TABLE */}
          <div className="space-y-4 not-prose my-10">
            <h3 className="text-xl sm:text-2xl font-heading font-black text-white tracking-tight">
              Summary: The 10 AI Visibility Audit Checks at a Glance
            </h3>
            <p className="text-sm text-slate-300">
              Use this quick reference matrix to identify the primary inspection tool, failure modes, and required action for each check:
            </p>

            <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[#080d19]">
              <table className="w-full text-left text-xs sm:text-sm text-slate-300">
                <thead className="bg-white/5 border-b border-white/10 font-mono text-cyan-300 uppercase tracking-wider text-[11px]">
                  <tr>
                    <th className="p-3.5">Check</th>
                    <th className="p-3.5">Primary Tool</th>
                    <th className="p-3.5">What to Inspect</th>
                    <th className="p-3.5">Action If Failing</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  <tr className="hover:bg-white/[0.02]">
                    <td className="p-3.5 font-bold text-white">01. Bot Access</td>
                    <td className="p-3.5 font-mono text-slate-400">robots.txt, WAF, Logs</td>
                    <td className="p-3.5">OAI-SearchBot, Claude-SearchBot</td>
                    <td className="p-3.5 text-cyan-300">Remove disallows &amp; whitelist bots</td>
                  </tr>
                  <tr className="hover:bg-white/[0.02]">
                    <td className="p-3.5 font-bold text-white">02. Indexability</td>
                    <td className="p-3.5 font-mono text-slate-400">Google Search Console</td>
                    <td className="p-3.5">Coverage errors, noindex, canonicals</td>
                    <td className="p-3.5 text-cyan-300">Fix directives &amp; request re-indexing</td>
                  </tr>
                  <tr className="hover:bg-white/[0.02]">
                    <td className="p-3.5 font-bold text-white">03. JS Rendering</td>
                    <td className="p-3.5 font-mono text-slate-400">View Source vs DOM</td>
                    <td className="p-3.5">Pricing, specs &amp; FAQs in raw HTML</td>
                    <td className="p-3.5 text-cyan-300">Enable SSR or pre-rendering</td>
                  </tr>
                  <tr className="hover:bg-white/[0.02]">
                    <td className="p-3.5 font-bold text-white">04. Question Maps</td>
                    <td className="p-3.5 font-mono text-slate-400">Customer Interview / RAG</td>
                    <td className="p-3.5">Fan-out journey coverage gap matrix</td>
                    <td className="p-3.5 text-cyan-300">Publish comprehensive answer pillars</td>
                  </tr>
                  <tr className="hover:bg-white/[0.02]">
                    <td className="p-3.5 font-bold text-white">05. Content Citability</td>
                    <td className="p-3.5 font-mono text-slate-400">Content Audit</td>
                    <td className="p-3.5">Atomic answers, tables &amp; H2 clarity</td>
                    <td className="p-3.5 text-cyan-300">Restructure for direct inverted pyramid</td>
                  </tr>
                  <tr className="hover:bg-white/[0.02]">
                    <td className="p-3.5 font-bold text-white">06. Canonicals &amp; Links</td>
                    <td className="p-3.5 font-mono text-slate-400">Screaming Frog / GSC</td>
                    <td className="p-3.5">Redirect chains, orphan URLs</td>
                    <td className="p-3.5 text-cyan-300">Point directly to 200 URLs</td>
                  </tr>
                  <tr className="hover:bg-white/[0.02]">
                    <td className="p-3.5 font-bold text-white">07. Business Entity</td>
                    <td className="p-3.5 font-mono text-slate-400">Schema Validator</td>
                    <td className="p-3.5">Organization, Person, sameAs links</td>
                    <td className="p-3.5 text-cyan-300">Add valid JSON-LD entity graph</td>
                  </tr>
                  <tr className="hover:bg-white/[0.02]">
                    <td className="p-3.5 font-bold text-white">08. Evidence &amp; E-E-A-T</td>
                    <td className="p-3.5 font-mono text-slate-400">Manual Review</td>
                    <td className="p-3.5">Case studies, metrics &amp; limitations</td>
                    <td className="p-3.5 text-cyan-300">Publish quantifiable client outcomes</td>
                  </tr>
                  <tr className="hover:bg-white/[0.02]">
                    <td className="p-3.5 font-bold text-white">09. Competitor SERP</td>
                    <td className="p-3.5 font-mono text-slate-400">ChatGPT, Claude, Gemini</td>
                    <td className="p-3.5">Cited competitor URLs &amp; features</td>
                    <td className="p-3.5 text-cyan-300">Bridge content &amp; evidence gaps</td>
                  </tr>
                  <tr className="hover:bg-white/[0.02]">
                    <td className="p-3.5 font-bold text-white">10. Measurement</td>
                    <td className="p-3.5 font-mono text-slate-400">GSC, Bing, GA4, Clarity</td>
                    <td className="p-3.5">Impressions, citations &amp; referrals</td>
                    <td className="p-3.5 text-cyan-300">Track 5 streams weekly for 12 weeks</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Section: Final Thoughts */}
          <section className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-heading font-black text-white tracking-tight">
              Final Thoughts: Building an Unshakeable AI Search Moat
            </h2>

            <p>
              Improving your AI visibility is not about stuffing keywords, buying black-hat citations, or chasing one secret GEO hack.
            </p>

            <p>
              It begins with fundamentals: understanding whether your website can be crawled by modern search agents, whether your pages are indexable, whether client-side JavaScript is hiding answers, and whether your content delivers clear, useful, and verifiable answers to the questions buyers ask.
            </p>

            <p>
              Start with confirmed technical blockers first, then prioritize the high-intent fan-out content gaps that directly influence buying decisions. Finally, track your progress methodically across multiple search and referral streams.
            </p>

            <blockquote className="p-4 rounded-xl bg-[#0c1427] border-l-4 border-cyan-400 text-white font-medium not-italic my-4">
              <strong>The Ultimate Goal:</strong> You aren&apos;t just making your website AI-ready—you are making your business the easiest, most trustworthy choice to recommend when potential customers turn to AI to make buying decisions.
            </blockquote>
          </section>


          {/* FAQ SECTION (Accordion + Semantic Schema) */}
          <div className="space-y-4 not-prose my-12">
            <div className="flex items-center gap-2 text-xs font-mono font-bold text-cyan-400 uppercase tracking-widest">
              <HelpCircle className="w-4 h-4" />
              <span>Frequently Asked Questions</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-heading font-black text-white tracking-tight">
              Common Questions About Auditing for AI Search
            </h3>

            <div className="space-y-3 mt-4">
              {[
                {
                  q: "What is an AI visibility audit and how is it different from a traditional SEO audit?",
                  a: "A traditional SEO audit evaluates keyword density, backlinks, crawl errors, and metadata aimed at ranking blue links in Google Search. An AI visibility audit assesses whether conversational answer engines (like ChatGPT Search, Google AI Overviews, Claude, and Perplexity) can crawl your pages (OAI-SearchBot), extract content without JavaScript rendering friction, verify your corporate entity, and cite your answers when users conduct multi-step fan-out research."
                },
                {
                  q: "Will allowing OAI-SearchBot allow OpenAI to scrape my content for model training?",
                  a: "No. OpenAI maintains two separate crawlers. OAI-SearchBot is strictly used for real-time ChatGPT Search retrieval to index and cite current pages. GPTBot is the separate crawler used for model training. You can allow OAI-SearchBot in robots.txt while disallowing GPTBot if you wish to be cited in ChatGPT Search without contributing to training datasets."
                },
                {
                  q: "Why do my pages rank #1 on Google but never get mentioned in ChatGPT or Claude?",
                  a: "Ranking on Google and being cited by AI models rely on different retrieval signals. Google matches keyword pages and algorithmic PageRank. AI answer engines synthesize answers by parsing raw HTML, verifying entity consensus across multiple web sources, evaluating customer buying questions (fan-out queries), and prioritizing atomic information gain. If your site blocks AI crawlers or locks data behind JavaScript, you remain invisible to LLMs."
                },
                {
                  q: "How does JavaScript client-side rendering hurt AI visibility?",
                  a: "While Googlebot queues pages for headless Chromium rendering, many real-time AI retrieval pipelines and fast answer bots only process the initial raw HTML response to minimize response latency. If your core pricing, specifications, comparison tables, or FAQs require client-side hydration to appear, those bots only see empty containers."
                },
                {
                  q: "How can I get Narendra to audit my website?",
                  a: "You can book a complete done-for-you AI Visibility Audit for a flat ₹5,000 (~$60 USD). You'll receive a full 4-layer diagnostic report within 72 hours, high-priority customer fan-out topics tailored to what you sell, a pre-publishing checklist for your team, and two 1-on-1 strategy video calls. Visit narendradigital.in/ai-visibility-audit/ to book your slot."
                }
              ].map((faq, idx) => (
                <div
                  key={idx}
                  className="rounded-2xl border border-white/10 bg-[#080d19] overflow-hidden transition-all"
                >
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full p-4 sm:p-5 text-left flex items-center justify-between gap-4 font-heading font-bold text-white text-sm sm:text-base hover:text-cyan-300 transition-colors cursor-pointer"
                  >
                    <span>{faq.q}</span>
                    <ChevronRight
                      className={`w-4 h-4 text-cyan-400 shrink-0 transition-transform duration-200 ${
                        openFaq === idx ? "rotate-90" : ""
                      }`}
                    />
                  </button>
                  {openFaq === idx && (
                    <div className="px-4 pb-4 sm:px-5 sm:pb-5 text-xs sm:text-sm text-slate-300 leading-relaxed border-t border-white/5 pt-3">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* AUTHOR BIO FOOTER BOX */}
          <div className="p-6 sm:p-7 rounded-2xl bg-[#090e1c] border border-white/10 not-prose my-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <img
              src="/narendra-avatar.jpg"
              alt="Kanna Narendra Kumar"
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl border-2 border-cyan-400/50 object-cover shrink-0"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/narendra-hero.jpg";
              }}
            />
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="font-heading font-bold text-white text-base sm:text-lg">
                  Kanna Narendra Kumar
                </h4>
                <span className="text-xs font-mono text-cyan-400 font-bold px-2.5 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/20">
                  Senior SEO &amp; AI Search Strategist
                </span>
              </div>
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                Senior SEO Specialist with 8+ years scaling organic pipeline and high-intent inbound acquisition for global B2B SaaS and enterprise brands. Specialized in Technical SEO, clean architecture, and next-generation Answer Engine Optimization (AEO · GEO · HEO).
              </p>
              <div className="flex items-center gap-4 text-xs font-mono pt-1">
                <a
                  href="https://www.linkedin.com/in/narendra-kumar-kanna/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1"
                >
                  <span>Connect on LinkedIn</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
                <span className="text-slate-600">•</span>
                <Link
                  to="/ai-visibility-audit/"
                  className="text-cyan-400 hover:text-cyan-300 underline font-bold"
                >
                  Request an AI Audit (₹5,000)
                </Link>
              </div>
            </div>
          </div>

          {/* RELATED ARTICLES SECTION */}
          <div className="pt-8 border-t border-white/10 not-prose">
            <div className="flex items-center justify-between mb-6">
              <div>
                <span className="text-xs font-mono text-cyan-400 font-bold uppercase tracking-wider">
                  Further Reading &amp; Frameworks
                </span>
                <h3 className="font-heading font-black text-xl sm:text-2xl text-white tracking-tight mt-1">
                  Related AI Search Articles
                </h3>
              </div>
              <Link
                to="/blog/"
                className="text-xs font-mono text-cyan-400 hover:text-cyan-300 flex items-center gap-1 font-bold"
              >
                <span>All Articles</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link
                to="/blog/how-to-do-keyword-research-for-ai-visibility/"
                className="p-5 rounded-2xl bg-white/[0.02] hover:bg-white/[0.05] border border-white/10 hover:border-cyan-500/40 transition-all flex flex-col justify-between group"
              >
                <div className="space-y-2">
                  <span className="text-[10px] font-mono text-cyan-400 font-bold uppercase tracking-wider">
                    Customer Intent &amp; AI Visibility
                  </span>
                  <h4 className="font-heading font-bold text-white text-sm group-hover:text-cyan-300 transition-colors line-clamp-2">
                    How to Do Keyword Research for AI Visibility: A Customer-Intent-First Approach
                  </h4>
                  <p className="text-xs text-slate-400 line-clamp-3">
                    Build a Question Memory from GSC, Reddit, customer reviews, and sales calls to rank in Google and gain citations in AI answers.
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-[11px] font-mono text-slate-500">
                  <span>10 min read</span>
                  <span className="text-cyan-400 group-hover:translate-x-0.5 transition-transform">Read &rarr;</span>
                </div>
              </Link>

              <Link
                to="/blog/how-to-measure-ai-search-visibility-for-free/"
                className="p-5 rounded-2xl bg-white/[0.02] hover:bg-white/[0.05] border border-white/10 hover:border-cyan-500/40 transition-all flex flex-col justify-between group"
              >
                <div className="space-y-2">
                  <span className="text-[10px] font-mono text-cyan-400 font-bold uppercase tracking-wider">
                    Measurement Playbook
                  </span>
                  <h4 className="font-heading font-bold text-white text-sm group-hover:text-cyan-300 transition-colors line-clamp-2">
                    How to Measure AI Search Visibility for Free (Without Paid Tools)
                  </h4>
                  <p className="text-xs text-slate-400 line-clamp-3">
                    Track GSC generative AI impressions, Bing Copilot citations, server logs, and GA4 referrals in one free spreadsheet.
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-[11px] font-mono text-slate-500">
                  <span>8 min read</span>
                  <span className="text-cyan-400 group-hover:translate-x-0.5 transition-transform">Read &rarr;</span>
                </div>
              </Link>

              <Link
                to="/blog/why-do-i-rank-on-google-but-dont-appear-in-chatgpt/"
                className="p-5 rounded-2xl bg-white/[0.02] hover:bg-white/[0.05] border border-white/10 hover:border-cyan-500/40 transition-all flex flex-col justify-between group"
              >
                <div className="space-y-2">
                  <span className="text-[10px] font-mono text-cyan-400 font-bold uppercase tracking-wider">
                    Strategic Analysis
                  </span>
                  <h4 className="font-heading font-bold text-white text-sm group-hover:text-cyan-300 transition-colors line-clamp-2">
                    Why Do I Rank on Google but Don&apos;t Appear in ChatGPT?
                  </h4>
                  <p className="text-xs text-slate-400 line-clamp-3">
                    Unpack why competitors appear in ChatGPT search while you don&apos;t, OAI-SearchBot mechanics, and fan-out query architecture.
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-[11px] font-mono text-slate-500">
                  <span>9 min read</span>
                  <span className="text-cyan-400 group-hover:translate-x-0.5 transition-transform">Read &rarr;</span>
                </div>
              </Link>

              <Link
                to="/blog/can-ai-search-engines-crawl-understand-cite-website/"
                className="p-5 rounded-2xl bg-white/[0.02] hover:bg-white/[0.05] border border-white/10 hover:border-cyan-500/40 transition-all flex flex-col justify-between group"
              >
                <div className="space-y-2">
                  <span className="text-[10px] font-mono text-cyan-400 font-bold uppercase tracking-wider">
                    Technical Architecture
                  </span>
                  <h4 className="font-heading font-bold text-white text-sm group-hover:text-cyan-300 transition-colors line-clamp-2">
                    Can AI Search Engines Crawl, Understand and Cite Your Website?
                  </h4>
                  <p className="text-xs text-slate-400 line-clamp-3">
                    The 4-layer technical foundation for AI SEO, AEO, and GEO before attempting content optimization.
                  </p>
                </div>
                <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-[11px] font-mono text-slate-500">
                  <span>11 min read</span>
                  <span className="text-cyan-400 group-hover:translate-x-0.5 transition-transform">Read &rarr;</span>
                </div>
              </Link>
            </div>
          </div>

          {/* Collaboration CTA Footer */}
          <section className="mt-10 p-8 sm:p-10 rounded-3xl bg-[#0c1222] border border-white/10 text-center space-y-4 not-prose">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 border border-cyan-500/30 flex items-center justify-center mx-auto text-cyan-400">
              <Zap className="w-6 h-6" />
            </div>
            <h3 className="font-heading font-black text-2xl sm:text-3xl text-white tracking-tight uppercase">
              Ready to Audit Your Website&apos;s AI Visibility?
            </h3>
            <p className="text-sm text-slate-300 max-w-xl mx-auto leading-relaxed">
              Get a complete 4-layer technical &amp; content diagnostic for ChatGPT Search, Google AI Overviews, Claude, and Perplexity—delivered within 72 hours.
            </p>
            <div className="pt-2 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/ai-visibility-audit/"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl btn-bold-primary text-white text-xs font-bold uppercase tracking-wider shadow-lg shadow-cyan-500/25 transition-transform hover:-translate-y-0.5 cursor-pointer"
              >
                <Sparkles className="w-4 h-4" />
                <span>Get an AI Visibility Audit</span>
                <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/#contact"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-2xl bg-white/[0.05] hover:bg-white/[0.1] border border-cyan-500/30 text-white text-xs font-bold uppercase tracking-wider transition-colors"
              >
                <span>Schedule Strategy Discussion</span>
                <ArrowUpRight className="w-4 h-4 text-cyan-400" />
              </Link>
            </div>
          </section>

        </article>
      </div>
    </div>
  );
}
